import { useEffect, useRef, useState } from "react";
import { play } from "./sound";

/**
 * Countdown timer with an animated SVG ring.
 *
 * - `key` should change between turns to reset; otherwise the same instance
 *   continues counting.
 * - `onExpire` fires exactly once when the timer hits zero.
 * - Honors `prefers-reduced-motion`: ring still updates but transitions are
 *   instant.
 * - Pass `paused` to freeze the countdown (e.g. while showing a result card).
 */
export default function TurnTimer({
  seconds,
  paused = false,
  onExpire,
  size = 56,
}: {
  seconds: number;
  paused?: boolean;
  onExpire: () => void;
  size?: number;
}) {
  const [remaining, setRemaining] = useState(seconds);
  const expiredRef = useRef(false);
  // Track which whole-second mark we last ticked on, so the SFX fires at most
  // once per second even though we update `remaining` every animation frame.
  const lastTickSecondRef = useRef<number>(Math.ceil(seconds));

  useEffect(() => {
    setRemaining(seconds);
    expiredRef.current = false;
    lastTickSecondRef.current = Math.ceil(seconds);
  }, [seconds]);

  useEffect(() => {
    if (paused || expiredRef.current) return;
    const startedAt = performance.now();
    const startRemaining = remaining;
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = (now - startedAt) / 1000;
      const next = Math.max(0, startRemaining - elapsed);
      setRemaining(next);
      // Synchronized tick SFX - fires once per whole second crossed.
      const secondMark = Math.ceil(next);
      if (
        secondMark < lastTickSecondRef.current &&
        secondMark > 0 &&
        // only tick during the final stretch so it doesn't get noisy on long timers
        secondMark <= Math.min(10, Math.ceil(seconds))
      ) {
        lastTickSecondRef.current = secondMark;
        play(secondMark <= 3 ? "tickFast" : "tick");
      }
      if (next <= 0) {
        if (!expiredRef.current) {
          expiredRef.current = true;
          play("expire");
          onExpire();
        }
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // We deliberately do NOT depend on `remaining` - it would restart the
    // RAF loop every frame. The loop closes over the value at start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, onExpire, seconds]);

  const radius = size / 2 - 4;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(1, remaining / seconds));
  const dashOffset = circumference * (1 - pct);
  const display = Math.ceil(remaining);
  const danger = remaining <= 3;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      role="timer"
      aria-live="off"
      aria-label={`${display} seconds remaining`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={3}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={danger ? "hsl(0 70% 55%)" : "hsl(var(--primary))"}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="transition-[stroke-dashoffset] duration-100 motion-reduce:transition-none"
        />
      </svg>
      <span
        className={`absolute text-sm font-bold font-mono tabular-nums ${
          danger ? "text-destructive" : "text-foreground"
        }`}
        aria-hidden="true"
      >
        {display}
      </span>
    </div>
  );
}